import { useState, useCallback, useEffect } from "react";
import { UseMarketplaceAppFormReturn } from "../../types";

function useMarketplaceAppForm({
  schema,
  initialValues = {},
}: {
  schema: any;
  initialValues?: any;
}): UseMarketplaceAppFormReturn {
  const [formData, setFormData] = useState<{ [key: string]: any }>(
    initialValues,
  );
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});

  useEffect(() => {
    if (schema?.properties) {
      const defaults: { [key: string]: any } = {};
      Object.keys(schema.properties).forEach((key) => {
        if (
          formData[key] === undefined &&
          schema.properties[key].default !== undefined
        ) {
          defaults[key] = schema.properties[key].default;
        }
      });
      if (Object.keys(defaults).length > 0) {
        setFormData((prev: { [key: string]: any }) => ({
          ...prev,
          ...defaults,
        }));
      }
    }
  }, [schema, formData]);

  const validateField = useCallback(
    (key: string, value: any): string[] => {
      const fieldErrors: string[] = [];
      if (!schema || !schema.properties || !schema.properties[key])
        return fieldErrors;

      const property = schema.properties[key];
      const fieldTitle = property.title || key;

      if (
        schema.required &&
        Array.isArray(schema.required) &&
        schema.required.includes(key)
      ) {
        if (!value) {
          fieldErrors.push(`${fieldTitle} is required. \n`);
          return fieldErrors;
        }
      }

      if (value !== undefined && value !== null && value !== "") {
        if (property.type === "string") {
          if (typeof value !== "string") {
            fieldErrors.push(`${fieldTitle} should be a string.\n`);
          }
          if (property.minLength && value.length < property.minLength) {
            fieldErrors.push(
              `${fieldTitle} should be at least ${property.minLength} characters. \n`,
            );
          }
          if (property.maxLength && value.length > property.maxLength) {
            fieldErrors.push(
              `${fieldTitle} should be at most ${property.maxLength} characters.\n`,
            );
          }
          if (property.pattern) {
            const regex = new RegExp(property.pattern);
            if (!regex.test(value)) {
              fieldErrors.push(
                `${fieldTitle} does not match the required pattern.\n`,
              );
            }
          }
          if (property.enum && !property.enum.includes(value)) {
            fieldErrors.push(
              `${fieldTitle} should be one of ${property.enum.join(", ")}.`,
            );
          }
        } else if (property.type === "integer" || property.type === "number") {
          const numValue = Number(value);
          if (isNaN(numValue)) {
            fieldErrors.push(`${fieldTitle} should be a number. \n`);
          } else {
            if (property.minimum !== undefined && numValue < property.minimum) {
              fieldErrors.push(
                `${fieldTitle} should be at least ${property.minimum}.\n`,
              );
            }
            if (property.maximum !== undefined && numValue > property.maximum) {
              fieldErrors.push(
                `${fieldTitle} should be at most ${property.maximum}.\n`,
              );
            }
          }
        }
      }
      return fieldErrors;
    },
    [schema],
  );

  const handleChange = useCallback(
    (key: string, value: any) => {
      setFormData((prev: { [key: string]: any }) => ({
        ...prev,
        [key]: value,
      }));
      const fieldErrs = validateField(key, value);
      setErrors((prev) => ({ ...prev, [key]: fieldErrs }));
    },
    [validateField],
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: { [key: string]: string[] } = {};
    if (schema) {
      if (Array.isArray(schema.required)) {
        schema.required.forEach((field: string) => {
          if (!formData[field]) {
            const fieldTitle = schema.properties?.[field]?.title || field;
            newErrors[field] = newErrors[field] || [];
            newErrors[field].push(`${fieldTitle} is required.`);
          }
        });
      }
      if (schema.properties) {
        Object.keys(schema.properties).forEach((key) => {
          const fieldErrs = validateField(key, formData[key]);
          if (fieldErrs.length > 0) {
            newErrors[key] = fieldErrs;
          }
        });
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [schema, formData, validateField]);

  return { formData, errors, handleChange, validateForm, setFormData };
}

export default useMarketplaceAppForm;
